import type { LocaleObject } from '@nuxtjs/i18n'
import type { UseDateFormatOptions } from '@vueuse/core'
import type { ClassValue } from 'clsx'
import type { TContinentCode, TCountryCode, TLanguageCode } from 'countries-list'
import { clsx } from 'clsx'
import { getCountryData, languages as languagesList } from 'countries-list'
import { defu } from 'defu'
import { withExtendedShadows } from 'tailwind-extended-shadows-merge'
import { extendTailwindMerge } from 'tailwind-merge'

interface CvtLocaleObject extends LocaleObject {
  iso: string
  iso2: TCountryCode
  lang: string
  native: string
  continent: TContinentCode
  currency: string[]
}

export default () => {
  const { locale, locales } = useI18n()

  const selectedLocale = computed(() => {
    return (locales.value as CvtLocaleObject[]).find(
      i => i.code === locale.value,
    )!
  })

  // Computed, not a one-off assignment: an in-session locale switch has to
  // change the country everything else derives from - currency, and the market
  // `useMarketFeatures()` resolves flags against for a visitor who has not
  // signed in. A signed-in customer's market comes from their session instead,
  // pinned at login, so that it cannot disagree with what the server enforces.
  const currentCountryData = computed(() => getCountryData(selectedLocale.value.iso2))

  const currentCountry = computed(() => {
    const country = currentCountryData.value

    return {
      code: country.iso2.toLowerCase(),
      language_code: locale.value,
      ...country,
      languages_data: country.languages.map((lang: TLanguageCode) => ({
        id: lang,
        code: `${lang}-${country.iso2}`,
        ...languagesList[lang],
        name: `${languagesList[lang]?.name} (${country.name})`,
        native: `${languagesList[lang]?.native} (${country.native})`,
      })),
    }
  })

  const cmerge = (...inputs: ClassValue[]) => {
    const twMerge = extendTailwindMerge(withExtendedShadows)
    return twMerge(clsx(inputs))
  }

  const formatCurrency = (value: number, currency?: string) => {
    return new Intl.NumberFormat(selectedLocale.value.code, {
      style: 'currency',
      currency: currency || currentCountryData.value.currency[0],
    }).format(value)
  }

  const formattedDate = (date: any, formatStr?: MaybeRefOrGetter<string>, options?: UseDateFormatOptions) => {
    const opts = defu(options, { locales: selectedLocale?.value?.code })
    const format = formatStr || 'DD MMMM YYYY'

    return useDateFormat(date, format, opts)
  }

  return {
    selectedLocale,
    currentCountryData,
    currentCountry,
    cmerge,
    formatCurrency,
    formattedDate,
  }
}
